import React from 'react';
import {
  Flex,
  useColorMode,
  Text,
  typography,
  color,
  Link,
  FlexProps,
} from 'deepstash-ui';
import NextLink from '../navigation/NextLink';

import { StoryApiResponse } from 'api/api.types';
import { useRouter } from 'next/router'; // Use it instead of own useRouter for fixing scroll problems
import { slugify } from 'utils/global';
import NextImage from '../image/NextImage';

const STORY_IMAGE_SIZE = { WIDTH: 260, HEIGHT: 180, BORDER_RADIUS: 8 };

interface StoryCardProps {
  story: StoryApiResponse;
  imageWidth?: number | string;
  imageHeight?: number;
  imageBorderRadius?: number;
}

const StoryCard: React.FC<StoryCardProps & FlexProps> = ({
  story,
  imageWidth = STORY_IMAGE_SIZE.WIDTH,
  imageHeight = STORY_IMAGE_SIZE.HEIGHT,
  imageBorderRadius = STORY_IMAGE_SIZE.BORDER_RADIUS,
  ...props
}) => {
  const { colorMode } = useColorMode();
  const router = useRouter();

  const storySlug = React.useMemo(() => {
    return slugify(story.title);
  }, [story.title]);

  const storyPageRoute = `/story/${story.id}/${storySlug}`;

  return (
    <Flex flexDirection="column" width={imageWidth} {...props}>
      <NextImage
        wrapperStyle={{
          width: imageWidth,
          height: imageHeight,
          borderRadius: imageBorderRadius,
          cursor: 'pointer',
        }}
        imageUrl={story.image}
        blurhash={story.image_blurhash}
        alt={`${story.title} Image`}
        priority={false}
        onClick={() => {
          router.push(storyPageRoute);
        }}
      />
      <Text
        {...typography.primaryTextFonts.small.regular}
        color={color[colorMode].textSecondary}
        mt={8}
      >
        {`${story.time_to_read} min read`}
      </Text>
      <NextLink href={storyPageRoute} passHref>
        <Link _focus={undefined} _hover={undefined}>
          <Text
            {...typography.primaryTextFonts.large.bold}
            color={color[colorMode].text}
            noOfLines={3}
            mt={16}
          >{`${story.title}`}</Text>
        </Link>
      </NextLink>
      <Text
        {...typography.primaryTextFonts.medium.regular}
        color={color[colorMode].textSecondary}
        noOfLines={3}
        mt={16}
      >{`${story.intro}`}</Text>
    </Flex>
  );
};

export default StoryCard;
